// "Notifikationer" in the account menu: a person chooses how often, and about which findings, NordComply writes to them.
// At most one mail a day, however many findings or companies. Off until the person turns it on.
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { getNotificationPrefs, saveNotificationPrefs } from '../api';
import { AREA_LABEL } from '../data/areaLabels';
import './PortalUI.css';

const FREQUENCY = [['off', 'Aldrig'], ['daily', 'Hver dag'], ['weekly', 'Hver uge']];
const HINT = {
  off: 'Du får ingen mails om nye fund.',
  daily: 'Højst én mail om dagen om morgenen, og kun hvis der er nye fund.',
  weekly: 'Én mail om mandagen, og kun hvis der er nye fund.',
};

// A kind of finding: a small switch in a rounded box.
function Toggle({ on, onChange, children, disabled }) {
  return (
    <button type="button" role="switch" aria-checked={on} disabled={disabled} className={`pu-tgr${on ? ' is-on' : ''}`} onClick={() => onChange(!on)}>
      <i />
      {children}
    </button>
  );
}

// currentTenant: set in a customer's portal. There the person cannot pick other customers: the notifications are about this one.
export default function NotificationsModal({ onClose, currentTenant = null }) {
  const [state, setState] = useState({ loading: true, error: '' });
  const [frequency, setFrequency] = useState('off');
  const [types, setTypes] = useState({ bad: true, warn: false });
  const [selected, setSelected] = useState(() => new Set());
  const [areas, setAreas] = useState(() => new Set());
  const [tenants, setTenants] = useState([]);
  const [email, setEmail] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ tone: '', text: '' });

  useEffect(() => {
    let cancelled = false;
    getNotificationPrefs()
      .then((d) => {
        if (cancelled) return;
        if (d.error) { setState({ loading: false, error: d.error }); return; }
        setFrequency(d.prefs.frequency);
        setTypes({ bad: !!d.prefs.types?.bad, warn: !!d.prefs.types?.warn });
        setSelected(new Set(d.prefs.tenantIds));
        // A person who has never chosen gets every area; the list that is saved is only ever what is ticked.
        setAreas(new Set(d.prefs.areas && d.prefs.areas.length > 0 ? d.prefs.areas : Object.keys(AREA_LABEL)));
        setTenants(d.tenants || []);
        setEmail(d.email || '');
        setState({ loading: false, error: '' });
      })
      .catch((err) => !cancelled && setState({ loading: false, error: err.message }));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const many = tenants.length > 1 && !currentTenant;
  const here = currentTenant ? tenants.find((x) => x.id === currentTenant.id || x.tenantId === currentTenant.tenantId) : null;
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? tenants.filter((t) => String(t.name).toLowerCase().includes(q)) : tenants;
  }, [tenants, search]);
  const off = frequency === 'off';

  const toggleTenant = (id, on) => setSelected((prev) => { const next = new Set(prev); if (on) next.add(id); else next.delete(id); return next; });
  const allShownOn = shown.length > 0 && shown.every((t) => selected.has(t.tenantId));
  const toggleShown = () => setSelected((prev) => { const next = new Set(prev); shown.forEach((t) => (allShownOn ? next.delete(t.tenantId) : next.add(t.tenantId))); return next; });

  async function save() {
    setSaving(true);
    setMessage({ tone: '', text: '' });
    try {
      // A person with one company follows that one without having to pick it.
      // In a customer's portal this customer is added (or kept) and the person's other choices are left as they were.
      const tenantIds = currentTenant ? (here && !off ? [...new Set([...selected, here.tenantId])] : [...selected]) : many ? [...selected] : tenants.map((x) => x.tenantId);
      if (!off && areas.size === 0) { setMessage({ tone: 'error', text: 'Vælg mindst ét område.' }); setSaving(false); return; }
      // Every area ticked is saved as the empty list, which the server reads as "all", so new areas are included later.
      const result = await saveNotificationPrefs({ frequency, types, tenantIds, areas: areas.size === Object.keys(AREA_LABEL).length ? [] : [...areas] });
      if (result.success) setMessage({ tone: 'ok', text: off ? 'Gemt. Du får ingen mails om nye fund.' : 'Gemt.' });
      else setMessage({ tone: 'error', text: result.error || 'Kunne ikke gemme. Prøv igen.' });
    } catch (err) {
      setMessage({ tone: 'error', text: 'Kunne ikke gemme. Prøv igen.' });
    } finally {
      setSaving(false);
    }
  }

  // Rendered on the body: the sidebar it is opened from is its own stacking context, which put the window behind the
  // cards on Overblik whatever the z-index said.
  const oneName = here ? here.name : tenants[0]?.name;
  const toggleArea = (key) => setAreas((prev) => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  return createPortal(
    <div className="pu-overlay" onClick={onClose}>
      <div className="pu-modal pu-modal--s" role="dialog" aria-label="Notifikationer" onClick={(e) => e.stopPropagation()}>
        <div className="pu-mh">
          <span className="pu-ico">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 8a6 6 0 0112 0c0 7 3 9 3 9H3s3-2 3-9M10 21h4" /></svg>
          </span>
          <div>
            <h2>Notifikationer</h2>
            <small>{many ? 'Én mail om nye fund hos de kunder, du vælger.' : oneName ? `Få en mail, når der kommer nye fund hos ${oneName}.` : 'Få en mail, når der kommer nye fund.'}</small>
          </div>
          <button type="button" className="pu-x" onClick={onClose} aria-label="Luk">×</button>
        </div>

        <div className="pu-mb">
          {state.loading && <p className="pu-msg">Henter…</p>}
          {state.error && <p className="pu-msg pu-msg--bad">{state.error}</p>}
          {!state.loading && !state.error && (
            <>
              <div className="pu-f">
                <span className="pu-lab">Hvor ofte</span>
                <div className="pu-seg pu-seg--w" role="group" aria-label="Hvor ofte">
                  {FREQUENCY.map(([key, label]) => (
                    <button key={key} type="button" className={frequency === key ? 'is-on' : ''} onClick={() => setFrequency(key)}>{label}</button>
                  ))}
                </div>
                <em>{HINT[frequency]}</em>
              </div>

              <div className="pu-f">
                <span className="pu-lab">Hvilke fund</span>
                <div className="pu-tg">
                  <Toggle on={types.bad} disabled={off} onChange={(v) => setTypes((x) => ({ ...x, bad: v }))}>Kræver handling</Toggle>
                  <Toggle on={types.warn} disabled={off} onChange={(v) => setTypes((x) => ({ ...x, warn: v }))}>Bør gennemgås</Toggle>
                </div>
              </div>

              <div className="pu-f">
                <span className="pu-lab">Områder <span className="pu-lab__acts"><button type="button" className="pu-link" disabled={off} onClick={() => setAreas(new Set(Object.keys(AREA_LABEL)))}>Vælg alle</button><button type="button" className="pu-link" disabled={off} onClick={() => setAreas(new Set())}>Fjern alle</button></span></span>
                <div className="pu-checks pu-checks--2">
                  {Object.entries(AREA_LABEL).map(([key, label]) => {
                    const on = areas.has(key);
                    return (
                      <button key={key} type="button" role="checkbox" aria-checked={on} disabled={off} className={`pu-ck${on ? ' is-on' : ''}`} onClick={() => toggleArea(key)}>
                        <i>{on && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>}</i>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <em>{areas.size} af {Object.keys(AREA_LABEL).length} områder valgt.</em>
              </div>

              {many && (
                <div className="pu-f">
                  <span className="pu-lab">Virksomheder <button type="button" className="pu-link" disabled={off} onClick={toggleShown}>{allShownOn ? 'Fravælg alle' : 'Vælg alle'}</button></span>
                  <input type="search" placeholder="Søg virksomhed ..." value={search} disabled={off} onChange={(e) => setSearch(e.target.value)} aria-label="Søg virksomhed" />
                  <div className="pu-checks pu-checks--2">
                    {shown.map((tn) => {
                      const on = selected.has(tn.tenantId);
                      return (
                        <button key={tn.tenantId} type="button" role="checkbox" aria-checked={on} disabled={off} className={`pu-ck${on ? ' is-on' : ''}`} onClick={() => toggleTenant(tn.tenantId, !on)}>
                          <i>{on && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>}</i>
                          {tn.name}
                        </button>
                      );
                    })}
                  </div>
                  {shown.length === 0 && <em>Ingen virksomheder matcher.</em>}
                  <em>{selected.size} af {tenants.length} valgt. Alle valgte samles i én mail.</em>
                </div>
              )}

              {!many && oneName && (
                <div className="pu-f">
                  <span className="pu-lab">Virksomhed</span>
                  <span className="pu-one"><span className="pu-av">{String(oneName).slice(0, 2).toUpperCase()}</span><b>{oneName}</b><small>Din eneste virksomhed</small></span>
                </div>
              )}
            </>
          )}
        </div>

        {!state.loading && !state.error && (
          <div className="pu-mf">
            <span className={`pu-msg${message.tone === 'error' ? ' pu-msg--bad' : message.tone === 'ok' ? ' pu-msg--ok' : ''}`}>{message.text || `Sendes til ${email}`}</span>
            <span className="pu-sp" />
            <button type="button" className="pu-btn" onClick={onClose}>Luk</button>
            <button type="button" className="pu-btn pu-btn--p" disabled={saving} onClick={save}>{saving ? 'Gemmer…' : 'Gem'}</button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
